/**
 * PWAInstallPrompt — devenu l'INVITATION AUX APPLIS DES STORES (06/10/2026).
 *
 *   « Je pense que le mieux c'est de les rediriger sur Apple Store et Play
 *   Store avec leur logo officiel ! La fenêtre doit être fermée dès que je
 *   clique sur la croix !! » — Lucas, 06/10/2026 (iOS 1.3.5 en ligne,
 *   Android 1.3.5 en production)
 *
 * Ce qui change : on ne propose PLUS d'installer la PWA (instructions « menu
 * Chrome → Ajouter à l'écran d'accueil ») mais l'appli native, par les badges
 * OFFICIELS non modifiés : Google Play sur Android, App Store sur iPhone, les
 * deux sur ordinateur. Sur Safari iPhone, c'est la bannière officielle d'Apple
 * (« Smart App Banner », `apple-itunes-app` dans public/index.html) qui s'en
 * charge — elle sait si l'appli est déjà installée (« Ouvrir ») : on ne double
 * pas avec la nôtre.
 *
 * La croix ferme pour de bon : une fois fermée, AUCUNE minuterie ne peut la
 * rouvrir dans la visite (`fermeeRef`), et le refus est mémorisé 30 jours.
 *
 * Historique (Phase 13.41)
 * ================================
 *
 * Prompt d'installation PWA, multi-plateforme + fallback robuste.
 *
 * Lucas (mai 2026) : "j'ai supprime Gively de ma page d'accueil sans
 * faire expres. Je reviens sur Gively en pensant que ça allait me
 * reproposer de telecharger. Ça ne le fait pas et je n'arrive pas
 * a la reinstaller depuis mon smartphone."
 *
 * == Bugs avant 13.41 ==
 *
 * 1. ``beforeinstallprompt`` ne se redeclenche pas TOUJOURS sur
 *    Chrome/Android apres desinstallation — bug connu navigateur.
 *    Si l'event ne vient jamais, on n'affichait RIEN.
 * 2. Cooldown apres dismiss : 7 jours = trop long si l'utilisateur
 *    veut reinstaller immediatement.
 * 3. Pas de detection ``getInstalledRelatedApps()`` pour verifier
 *    l'etat reel d'installation.
 * 4. iOS bien gere mais Android sans event = silence total.
 *
 * == Fix Phase 13.41 ==
 *
 * 1. Detection active via ``getInstalledRelatedApps()`` (Android
 *    Chrome 80+) — si l'app n'est PAS dans la liste des apps
 *    installees relatives au site, on peut forcer un fallback.
 * 2. Fallback Android : si Chrome detecte + pas standalone + pas
 *    installe + aucun ``beforeinstallprompt`` recu apres 6 secondes,
 *    on affiche QUAND MEME un prompt avec instructions manuelles
 *    (menu Chrome 3 points -> "Installer l'application" ou "Ajouter
 *    a l'ecran d'accueil").
 * 3. Cooldown reduit a 3 jours (vs 7).
 * 4. Compteur de visites sans install : si > 5 visites, on ignore
 *    le cooldown dismissed et on repropose plus tot. Les utilisateurs
 *    motives ne sont pas "punis" par leur premier dismiss.
 * 5. Section "Vous l'aviez deja installee ?" en bas du prompt qui
 *    explique en 1 phrase comment la reinstaller meme apres
 *    suppression manuelle de l'icone.
 */
import { useState, useEffect, useRef } from "react";
import { safeLocalStorage } from "../utils/safeStorage";
import { PLAY_PACKAGE_ID } from "../utils/storeLinks";
import { BadgeAppStore, BadgeGooglePlay } from "./StoreBadges";

const XIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Anti-relance (Phase 13.76, conservé) : 30 jours après un refus explicite,
// et JAMAIS plus de 2 auto-affichages dans la vie du navigateur. Clés NEUVES :
// l'invitation aux stores n'est pas l'ancienne bannière PWA — un visiteur qui
// avait épuisé ses 2 affichages PWA peut découvrir une fois l'appli native.
const CLE_REFUS = "store-prompt-dismissed";
const CLE_AFFICHAGES = "store-prompt-autoshown";
const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_AUTO_SHOWS = 2;
const DELAI_AFFICHAGE_MS = 5000;

// Contrat §12 « esthétisme d'abord » — jamais par-dessus un tunnel ou un
// espace de travail critique (Nightly A02). Source unique : utils/tunnelPaths.
export { TUNNEL_PATH_PREFIXES, isTunnelPath } from "../utils/tunnelPaths";
import { isTunnelPath as _estDansUnTunnel } from "../utils/tunnelPaths";

/**
 * Quelle(s) boutique(s) proposer. Pure, testée.
 *  - "native"  : déjà dans l'appli (Capacitor) → rien ;
 *  - "safari-ios" : Safari iPhone/iPad → rien, la Smart App Banner d'Apple s'en charge ;
 *  - "ios" | "android" | "desktop".
 */
export function plateformeStore(ua = "", { natif = false } = {}) {
  if (natif) return "native";
  const s = String(ua || "");
  if (/iPad|iPhone|iPod/.test(s)) {
    const autreNavigateur = /CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line\/|GSA\//.test(s);
    return autreNavigateur ? "ios" : "safari-ios";
  }
  if (/Android/i.test(s)) return "android";
  return "desktop";
}

/**
 * L'appli native est-elle DÉJÀ installée sur cet appareil ? (10/10/2026)
 *
 * `navigator.getInstalledRelatedApps()` (Chrome Android, contexte sécurisé, cadre
 * de premier niveau) rend les applis listées dans `related_applications` du
 * manifeste ET liées au site par Digital Asset Links (/.well-known/assetlinks.json,
 * en production depuis les App Links). Source : MDN, Navigator.getInstalledRelatedApps,
 * lu le 10/10/2026 — « could be used for … removing "install our app" banners from
 * the web app if the platform-specific app … is already installed ».
 * Fail-open : API absente, refusée ou en erreur → « pas installée » → on invite.
 */
export async function appliNativeDejaInstallee(nav = typeof navigator !== "undefined" ? navigator : {}) {
  try {
    if (!nav || typeof nav.getInstalledRelatedApps !== "function") return false;
    const apps = await nav.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.some((a) => a && (a.id === PLAY_PACKAGE_ID || a.platform === "play"));
  } catch (_) {
    return false;
  }
}

export const PWAInstallPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [plateforme, setPlateforme] = useState("desktop");
  const fermeeRef = useRef(false);

  useEffect(() => {
    let natif = false;
    try {
      natif = Boolean(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    } catch (_) { /* web */ }
    const p = plateformeStore(navigator.userAgent, { natif });
    setPlateforme(p);
    if (p === "native" || p === "safari-ios") return undefined;

    const affichages = parseInt(safeLocalStorage.getItem(CLE_AFFICHAGES) || "0", 10) || 0;
    if (affichages >= MAX_AUTO_SHOWS) return undefined;
    const refus = parseInt(safeLocalStorage.getItem(CLE_REFUS) || "", 10);
    if (!Number.isNaN(refus) && Date.now() - refus < DISMISS_COOLDOWN_MS) return undefined;
    // Le bandeau cookies passe d'abord : une fenêtre à la fois.
    if (!safeLocalStorage.getItem("gively_cookie_consent")) return undefined;

    let demonte = false;
    const minuterie = setTimeout(() => {
      if (fermeeRef.current) return;
      try {
        if (_estDansUnTunnel(window.location.pathname)) return; // sans consommer d'affichage
      } catch (_) { /* pas de location : on n'empêche pas */ }
      const afficher = () => {
        if (fermeeRef.current || demonte) return;
        const n = parseInt(safeLocalStorage.getItem(CLE_AFFICHAGES) || "0", 10) || 0;
        safeLocalStorage.setItem(CLE_AFFICHAGES, String(n + 1));
        setVisible(true);
      };
      // Appli déjà installée (Chrome Android) → on n'invite pas, et on ne
      // consomme aucun affichage. Sans l'API (iPhone, ordinateur, jsdom) : chemin
      // synchrone inchangé.
      if (typeof navigator !== "undefined" && typeof navigator.getInstalledRelatedApps === "function") {
        appliNativeDejaInstallee(navigator).then((installee) => { if (!installee) afficher(); });
      } else {
        afficher();
      }
    }, DELAI_AFFICHAGE_MS);
    return () => { demonte = true; clearTimeout(minuterie); };
  }, []);

  const fermer = () => {
    fermeeRef.current = true;
    setVisible(false);
    safeLocalStorage.setItem(CLE_REFUS, Date.now().toString());
  };

  // Un clic sur un badge : la personne part vers la boutique. On ne la relance plus.
  const versLaBoutique = () => {
    fermeeRef.current = true;
    setVisible(false);
    safeLocalStorage.setItem(CLE_AFFICHAGES, String(MAX_AUTO_SHOWS));
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[10000] animate-slide-up md:left-auto md:right-4 md:w-96" data-testid="pwa-install-prompt"
         role="dialog" aria-label="Télécharger l'application Gively">
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 p-4">
        {/* Croix : zone de 44 px (cible tactile recommandée), au-dessus de tout. */}
        <button type="button" onClick={fermer} aria-label="Fermer" data-testid="store-prompt-close"
                className="absolute top-1 right-1 z-10 w-11 h-11 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100">
          <XIcon className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 pr-10">
          <img src="/logo-gively.svg" alt="" aria-hidden="true" width={44} height={44} className="w-11 h-11 rounded-xl flex-shrink-0" />
          <div>
            <p className="font-bold text-slate-900 leading-tight">L'application Gively</p>
            <p className="text-sm text-slate-500 leading-snug">Annonces, messages et alertes, toujours sous la main.</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3" onClick={versLaBoutique}>
          {plateforme === "android" ? <BadgeGooglePlay /> : plateforme === "ios" ? <BadgeAppStore /> : (
            <><BadgeAppStore /><BadgeGooglePlay /></>
          )}
        </div>
      </div>
    </div>
  );
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out forwards;
  }
`;
document.head.appendChild(style);

export default PWAInstallPrompt;
